import { Box, Grid, styled } from '@mui/material';
import { Loader } from 'components/Forms';
import useActivity from 'hooks/useActivity';
import { ErrorState, FlexBox, PageContainer, PageSubTitle, PageTitle } from 'components/Structure';
import OverviewHeader from 'components/Structure/OverviewHeader/OverviewHeader';
import { CardIllustration, EmptyStateCard, ThemeButton, ThemeTypography } from 'designSystem';
import Image from 'designSystem/DataDisplay/Image/Image';
import Icon from 'designSystem/Primitives/Icon/Icon';
import React, { FC } from 'react';
import { ESiteType } from 'types/site.types';
import { SiteTypeLabels } from 'utils/site.utils';
import { ImageVariant } from 'types/media.types';
import { OverviewHeaderContainer } from 'components/Structure/OverviewHeader/OverviewHeader.styles';
import LogSummary from 'designSystem/DataDisplay/LogSummary/LogSummary';
import { AvailableSizes, BackgroundTypes, IllustrationTypes, ItemTypes } from 'types/enums';

const Tag = styled(FlexBox)(({ theme }) => ({
  background: theme.custom.colors.lightBorderColor,
  height: 21,
  padding: theme.spacing(1),
}));

const ActivityOverview: FC = () => {
  const { activity, latestLog, loading, error, handleEditActivity } = useActivity();

  if (loading) return <Loader />;
  if (error || !activity) return <ErrorState />;

  // Check if an image exists on activity
  const firstMediaWithImage = activity.mediaList.find(
    media => media.imageId || media.imageVariantId
  ) as ImageVariant | undefined;

  return (
    <PageContainer>
      <PageTitle
        autoBackNavigation
        title="Activity overview"
        goBackLabel="All activities"
        goBackUrl="/activities"
        titleExtension={activity.title}
      />
      <Box mb={3} />

      <Grid container spacing={3}>
        <Grid item xs={12} lg={12} xl={8}>
          <OverviewHeader image={firstMediaWithImage} placeholderIconName="activity-placeholder">
            <Box
              display="flex"
              alignItems="flex-start"
              flexDirection="column"
              height="100%"
              justifyContent="space-evenly"
            >
              <FlexBox>
                <Box mb={1}>
                  <ThemeTypography variant="TITLE_MEDIUM" autoOverflow maxWidth={550}>
                    {activity.title}
                  </ThemeTypography>
                </Box>

                {activity.component && (
                  <>
                    <Icon name="arrow-line-right" size="medium" mx={1} />
                    <Tag overflow="hidden">
                      <ThemeTypography variant="LABEL_INPUT" autoOverflow>
                        {activity.component.title}
                      </ThemeTypography>
                    </Tag>
                  </>
                )}
              </FlexBox>

              <FlexBox mb={1} gap={1}>
                {activity.site.siteType === ESiteType.CLUSTER && activity.site.sitesType && (
                  <FlexBox gap={0.5}>
                    <Icon name="farm" size="small" color="gray-80" />
                    <ThemeTypography variant="LABEL_INPUT" color="GRAY_80">
                      {activity.site.sites.count}{' '}
                      {SiteTypeLabels[activity.site.sitesType].toLowerCase()}
                      {activity.site.sites.count <= 1 ? '' : 's'}
                    </ThemeTypography>
                  </FlexBox>
                )}

                <FlexBox gap={0.5}>
                  <Icon name="location" size="small" color="gray-80" />
                  <ThemeTypography variant="LABEL_INPUT" color="GRAY_80">
                    {activity.site.locationName}
                  </ThemeTypography>
                </FlexBox>

                {activity.ownedBy && (
                  <FlexBox gap={0.5}>
                    <Image
                      image={activity.ownedBy?.logo}
                      backupIcon="partner"
                      width={12}
                      height={12}
                    />
                    <ThemeTypography variant="LABEL_INPUT" color="GRAY_80">
                      {activity.ownedBy.name}
                    </ThemeTypography>
                  </FlexBox>
                )}
              </FlexBox>

              <ThemeButton
                startIcon={<Icon name="edit" />}
                size="large"
                color="YELLOW"
                loading={false}
                onClick={() => handleEditActivity(activity)}
              >
                Edit activity
              </ThemeButton>
            </Box>
          </OverviewHeader>
        </Grid>

        {/* History of partner logs */}
        <Grid item xs={12} lg={6} xl={4}>
          <PageSubTitle title="History" mb={2} display={{ xl: 'none' }} />
          <OverviewHeaderContainer height={{ xl: '100%' }}>
            {activity.latestLog && (
              <LogSummary latestLog={latestLog} createdTimestamp={activity.createdTimestamp} />
            )}
          </OverviewHeaderContainer>
        </Grid>

        <Grid item xs={12}>
          <EmptyStateCard
            title="Use activity in your chains to map your supply chain and promote transparency"
            variant={ItemTypes.COMPONENT_CHAIN}
            buttonIcon={<Icon name="component-chain" />}
            buttonText="Add component chain"
            background={BackgroundTypes.WORLD}
            size={AvailableSizes.MEDIUM}
            renderIllustration={
              <CardIllustration
                image={IllustrationTypes.LOCATIONS}
                width={300}
                right={25}
                top={0}
              />
            }
          />
        </Grid>
      </Grid>
    </PageContainer>
  );
};

export default ActivityOverview;
